import React, { useEffect, useState } from "react";
import $ from "jquery";
import { Edit3, Image as ImageIcon, Save, Upload } from "lucide-react";
import ApiURl from "../../controllers/Api";

const EMPTY_FORM = {
  id: "",
  name: "",
  role: "",
  description: "",
  sort_order: 1,
  is_active: "1",
  image_url: "",
};

export default function AdminTeams() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await $.getJSON(`${ApiURl}getTeamMembers.php`);
      if (response?.success && Array.isArray(response.data)) {
        setMembers(response.data);
      }
    } catch (error) {
      console.error("Error loading team members:", error);
      setMessage("Could not load team members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setPreview("");
  };

  const editMember = (member) => {
    setForm({
      id: member.id || "",
      name: member.name || "",
      role: member.role || "",
      description: member.description || "",
      sort_order: member.sort_order || 1,
      is_active: String(member.is_active ?? 1),
      image_url: member.image_path || "",
    });
    setImageFile(null);
    setPreview(member.image_url || "");
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    if (imageFile) payload.append("image", imageFile);

    try {
      const response = await $.ajax({
        url: `${ApiURl}saveTeamMember.php`,
        type: "POST",
        data: payload,
        processData: false,
        contentType: false,
        dataType: "json",
      });

      if (response?.success) {
        setMessage(response.message || "Team member saved successfully.");
        resetForm();
        fetchMembers();
      } else {
        setMessage(response?.message || "Failed to save team member.");
      }
    } catch (error) {
      console.error("Error saving team member:", error);
      setMessage("Server error while saving team member.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-teams-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <div className="mx-auto mb-6 max-w-[1450px] rounded-[24px] border border-[#d8c6b7] bg-[radial-gradient(circle_at_top_left,rgba(212,175,55,0.14),transparent_34%),linear-gradient(135deg,#fffaf5,#f2e6dc)] p-5 shadow-[0_18px_48px_rgba(43,0,0,0.08)] sm:p-6">
        <h1 className="font-serif text-3xl font-medium tracking-tight text-[#2B0000] sm:text-4xl">Team Members</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-[#6f5e57]">
          Add, edit and control which team members appear on the public Teams page.
        </p>
      </div>

      {message && (
        <div className="mx-auto mb-6 max-w-[1450px] rounded-2xl border border-[#d4af37]/25 bg-[#fff7e6] px-4 py-3 text-sm font-semibold text-[#7d5a19] shadow-[0_10px_25px_rgba(43,0,0,0.05)]">
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mx-auto mb-8 max-w-[1450px] rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-serif text-2xl font-medium text-[#2B0000]">
            {form.id ? "Update Team Member" : "Add Team Member"}
          </h2>
          {form.id && (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
            >
              Cancel Edit
            </button>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.12fr_0.88fr]">
          <div className="space-y-4">
            <TextInput label="Name" value={form.name} onChange={(value) => updateForm("name", value)} required />
            <TextInput label="Role" value={form.role} onChange={(value) => updateForm("role", value)} required />
            <TextArea label="Description" value={form.description} onChange={(value) => updateForm("description", value)} rows={5} />

            <div className="grid gap-4 md:grid-cols-2">
              <TextInput label="Sort Order" type="number" value={form.sort_order} onChange={(value) => updateForm("sort_order", value)} />
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-[#4a352f]">Status</span>
                <select
                  value={form.is_active}
                  onChange={(event) => updateForm("is_active", event.target.value)}
                  className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </label>
            </div>
          </div>

          <div className="rounded-[20px] border border-[#decfc3] bg-[linear-gradient(145deg,#fffaf5,#f3e8dd)] p-4 shadow-[0_14px_36px_rgba(43,0,0,0.05)]">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#2B0000]">
              <ImageIcon className="h-4 w-4 text-[#8f6f38]" />
              Member Image
            </div>
            <div className="mb-4 h-72 overflow-hidden rounded-[18px] border border-[#d8c6b7] bg-[#fbf3eb] shadow-inner">
              {preview ? (
                <img src={preview} alt="Team member preview" className="h-full w-full bg-[#fffaf5] object-contain" />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-[#9b877e]">
                  No image selected
                </div>
              )}
            </div>
            <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm font-semibold text-[#2B0000] shadow-sm transition hover:border-[#c59a4a] hover:bg-[#fff7ed]">
              <Upload className="h-4 w-4" />
              Upload Image
              <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#2B0000] px-5 py-3 text-sm font-semibold text-[#f4dfae] shadow-[0_12px_28px_rgba(43,0,0,0.16)] transition hover:-translate-y-0.5 hover:bg-[#4A090B] disabled:cursor-not-allowed disabled:bg-[#b7a69b] disabled:text-white"
        >
          <Save className="h-4 w-4" />
          {saving ? "Saving..." : form.id ? "Update Member" : "Add Member"}
        </button>
      </form>

      <div className="mx-auto max-w-[1450px] overflow-hidden rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] shadow-[0_22px_60px_rgba(43,0,0,0.08)]">
        <div className="border-b border-[#d8c6b7] bg-[linear-gradient(135deg,#fffaf5,#f2e6dc)] px-4 py-4 sm:px-5">
          <h2 className="font-serif text-2xl font-medium text-[#2B0000]">Saved Team Members</h2>
        </div>
        <div className="divide-y divide-[#eadfd5]">
          {loading ? (
            <div className="p-6 text-sm text-[#7d6c64]">Loading team members...</div>
          ) : members.length === 0 ? (
            <div className="p-6 text-sm text-[#7d6c64]">No team members added yet.</div>
          ) : (
            members.map((member) => (
              <div key={member.id} className="grid gap-4 p-4 transition hover:bg-[#fff5ea] sm:p-5 md:grid-cols-[100px_minmax(0,1fr)_auto] md:items-center">
                <div className="h-24 w-24 overflow-hidden rounded-full border-4 border-white bg-[#fbf3eb] shadow-[0_10px_28px_rgba(43,0,0,0.12)] ring-1 ring-[#d8c6b7]">
                  {member.image_url && <img src={member.image_url} alt={member.name} className="h-full w-full bg-[#fffaf5] object-contain" />}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-serif text-xl font-medium text-[#2B0000]">{member.name}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${member.is_active ? "border border-[#cfe2d1] bg-[#edf7ee] text-[#47704e]" : "border border-[#dfd1c6] bg-[#f3ebe4] text-[#6d5a53]"}`}>
                      {member.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-semibold text-[#8f6f38]">{member.role}</p>
                  <p className="mt-1 line-clamp-2 text-sm leading-6 text-[#6f5e57]" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                    {member.description}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => editMember(member)}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:border-[#c59a4a] hover:bg-[#fff7ed]"
                >
                  <Edit3 className="h-4 w-4" />
                  Edit
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        .admin-teams-luxury {
          background:
            radial-gradient(circle at 7% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .admin-teams-luxury input,
        .admin-teams-luxury select,
        .admin-teams-luxury textarea,
        .admin-teams-luxury button {
          font: inherit;
        }

        .admin-teams-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .admin-teams-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.5) rgba(212,175,55,.07);
        }

        .admin-teams-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .admin-teams-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .admin-teams-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.5);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .admin-teams-luxury {
            padding: 10px;
          }
        }
      `}</style>
    </div>
  );
}

function TextInput({ label, value, onChange, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-[#4a352f]">{label}</span>
      <input
        type={type}
        required={required}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 4 }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-[#4a352f]">{label}</span>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
      />
    </label>
  );
}